/**
 * System Health — proactively surfaces data-quality problems (incomplete
 * devices, IP conflicts, devices without location/interface, subnets near
 * capacity) with a single health score. Reads GET /dashboard/health.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Chip, List, ListItemButton, ListItemText,
  Accordion, AccordionSummary, AccordionDetails, CircularProgress, Alert,
  LinearProgress, Button, Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from '../api/axios';

const SEV = {
  error: { color: 'error', icon: <ErrorIcon color="error" /> },
  warning: { color: 'warning', icon: <WarningIcon sx={{ color: 'warning.main' }} /> },
  info: { color: 'info', icon: <InfoIcon color="info" /> },
};

export default function SystemHealth() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get('/dashboard/health');
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось загрузить');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 2 }}><Alert severity="error" action={<Button onClick={load}>Повторить</Button>}>{error}</Alert></Box>;

  const score = data.score ?? 100;
  const scoreColor = score >= 85 ? 'success.main' : score >= 60 ? 'warning.main' : 'error.main';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Здоровье системы</Typography>
        <Button startIcon={<RefreshIcon />} onClick={load}>Обновить</Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h2" sx={{ color: scoreColor, fontWeight: 'bold' }}>{score}</Typography>
            <Typography color="text.secondary">индекс здоровья</Typography>
            <LinearProgress variant="determinate" value={score}
              sx={{ mt: 2, height: 8, borderRadius: 4,
                '& .MuiLinearProgress-bar': { bgcolor: scoreColor } }} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {data.issue_count === 0 ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleIcon color="success" fontSize="large" />
                <Typography variant="h6">Проблем не найдено — данные в порядке 🎉</Typography>
              </Stack>
            ) : (
              <Typography variant="h6">Найдено категорий проблем: {data.issue_count}</Typography>
            )}
            <Typography color="text.secondary" sx={{ mt: 1 }}>Всего устройств: {data.total_devices}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {data.issues.map((issue) => (
        <Accordion key={issue.key} defaultExpanded={issue.severity === 'error'}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
              {(SEV[issue.severity] || SEV.info).icon}
              <Typography sx={{ flexGrow: 1 }}>{issue.title}</Typography>
              <Chip label={issue.count} color={(SEV[issue.severity] || SEV.info).color} size="small" />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{issue.hint}</Typography>
            <List dense sx={{ maxHeight: 260, overflow: 'auto' }}>
              {issue.items.map((it, i) => (
                <ListItemButton key={i} onClick={() => navigate(it.url)}>
                  <ListItemText primary={it.name} />
                </ListItemButton>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      ))}

      {data.subnet_stats?.length > 0 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Заполнение подсетей</Typography>
          {data.subnet_stats.map((s) => (
            <Box key={s.id} sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">{s.name}</Typography>
                <Typography variant="body2" color="text.secondary">{s.used}/{s.capacity} ({s.pct}%)</Typography>
              </Box>
              <LinearProgress variant="determinate" value={Math.min(100, s.pct)}
                sx={{ height: 6, borderRadius: 3,
                  '& .MuiLinearProgress-bar': { bgcolor: s.pct >= 80 ? 'error.main' : s.pct >= 50 ? 'warning.main' : 'success.main' } }} />
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
}
